import { useEffect, useState } from 'react';

import analyticsService from '../../../services/analyticsService';
import normalizeCourseToc from '../../../utils/normalizeCourseToc';

import type { GetUserCourseTocWithProgressResponse } from '../../../types/courseToc';

type UsePracticeCourseTocReturn = {
  data: GetUserCourseTocWithProgressResponse | null;
  error: string | null;
  isLoading: boolean;
};

/**
 * Fetches the course ToC with progress for the LOGGED-IN student (TANAI-1869).
 *
 * Uses the session-scoped endpoint (`getSessionCourseToc`, user resolved from the
 * token) — NOT `useUserCourseToc`, which targets the teacher endpoint that takes an
 * explicit userId. Returns the same typed read-model that the lesson tree renders.
 */
function usePracticeCourseToc(courseId?: string | null): UsePracticeCourseTocReturn {
  const [data, setData] = useState<GetUserCourseTocWithProgressResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId) {
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function fetchToc(): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const response = await analyticsService.getSessionCourseToc(courseId);
        if (!cancelled) {
          setData(normalizeCourseToc(response, courseId));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchToc();

    return () => {
      cancelled = true;
    };
  }, [courseId]);

  return { data, error, isLoading };
}

export default usePracticeCourseToc;
